import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiBase, authHeaders, apiFetch, isApiDownResponse, apiDownError } from '../../utils/api';
import { errBox } from './ui';

// ── Inline viewer for the printable books and statements (owner 2026-10-13) ─────────────────────
// The server renders every book as print HTML (the PDF is that HTML through headless Chrome), and
// ?format=html hands the HTML back instead. A bare <iframe src="/api/…"> cannot carry the login
// token and 401s, so the page is fetched with authHeaders(), wrapped in a blob and shown from its
// blob URL. The URL is revoked on unmount or when a new one replaces it — NOT on the 60-second timer
// the open-in-tab buttons use, because an iframe that is still on screen would go blank.
export default function HtmlViewer({ base, path, qs, reload = 0, height = 760 }: {
  base: 'ledger-books' | 'accounting-extras';
  path: string;
  qs: Record<string, string>;
  reload?: number;
  height?: number;
}) {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const key = JSON.stringify(qs);

  useEffect(() => {
    let alive = true; let made = '';
    setLoading(true); setErr('');
    (async () => {
      try {
        const res = await apiFetch(`${apiBase()}/${base}/${path}?${new URLSearchParams({ ...qs, format: 'html' })}`, { headers: authHeaders() });
        if (isApiDownResponse(res)) throw apiDownError();   // a deploy's restart: "being updated — try again", and the notice
        if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as any).error || t('aw_viewer_failed'));
        const html = await res.text();
        if (!alive) return;
        // Typed explicitly: the iframe decides how to show the blob from its MIME type.
        made = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
        setUrl(made);
      } catch (e: any) { if (alive) setErr(String(e?.message || t('aw_viewer_failed'))); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; if (made) URL.revokeObjectURL(made); };
  }, [base, path, key, reload]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) return <div style={errBox}>{err}</div>;
  return (
    <div style={{ position: 'relative', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
      {loading && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)', background: 'var(--bg-surface)' }}><i className="ti ti-loader" style={{ fontSize: 20 }} /></div>}
      {url && <iframe title={path} src={url} style={{ width: '100%', height, border: 'none', display: 'block' }} />}
    </div>
  );
}
